import { useRef, useState } from 'react';
import { CirclePlus, Image, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Button } from 'components/ui/button';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { modelSupportedExtensionsMap } from '../types/model.types';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../components/core/theme-provider';
import { ButtonIds } from 'features/chat/types/chat';

export interface FileUploadPopoverProps {
  onFilesSelected?: (files: File[]) => void;
  isFeatureSelected?: Record<ButtonIds, boolean>;
  disabled?: boolean;
}

export function FileUploadPopover({
  onFilesSelected,
  isFeatureSelected,
  disabled = false,
}: FileUploadPopoverProps = {}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedModel = useModelStore((state) => state.selectedModel);
  const [clickOnFiles, setClickOnFiles] = useState(false);
  const { theme } = useTheme();
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      onFilesSelected && onFilesSelected(newFiles);
      setOpen(false); // Close the popover after selecting files
    }
  };

  const handleAddPhotosAndFilesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleAddPhotosClick = (e: React.MouseEvent) => {
    setTimeout(() => handleAddPhotosAndFilesClick(e), 100);
    setClickOnFiles(false);
  };

  const handleAddFilesClick = (e: React.MouseEvent) => {
    setTimeout(() => handleAddPhotosAndFilesClick(e), 100);
    setClickOnFiles(true);
  };

  function getAcceptData() {
    if (!selectedModel) {
      return '';
    }

    return clickOnFiles
      ? modelSupportedExtensionsMap[selectedModel?.modelType].document.join(',')
      : modelSupportedExtensionsMap[selectedModel?.modelType].image.join(',');
  }

  return (
    <div className="rounded-xl">
      <div className="xl:flex hidden items-center gap-5 ">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              disabled={disabled || (isFeatureSelected ? isFeatureSelected?.[ButtonIds.CREATE_IMAGE] : false)}
              size="icon"
              variant="ghost"
              className={`h-[34px] ${theme === 'dark' ? 'border-primary-200 ' : 'border-primary-600'} rounded-full border-[0.5px] w-[34px]`}
            >
              <Plus className={`h-5 w-5 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
              <span className="sr-only">{t('ADD_FILES')}</span>
            </Button>
          </PopoverTrigger>


          <PopoverContent className={` w-40 p-0 `} align="start" side="top" sideOffset={10}>
            <div className="flex flex-col p-1 gap-1">
              <button
                className={`flex items-center gap-3 px-2 py-1.5  transition-colors hover:bg-accent hover:text-accent-foreground  text-left disabled:opacity-50 ${selectedModel?.modelType === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handleAddPhotosClick}
                disabled={selectedModel?.modelType === 1}
              >
                <div className="flex h-4 w-4 items-center justify-center">
                  <Image className={`h-4 w-4 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                </div>
                <span className={`font-light text-sm leading-5 `} >{t('ADD_PHOTOS')}</span>
              </button>

              <button
                className="flex items-center gap-3 px-2 py-1.5 transition-colors hover:bg-accent hover:text-accent-foreground text-left disabled:opacity-50"
                onClick={handleAddFilesClick}
              >
                <div className="flex h-4 w-4 items-center justify-center">
                  <CirclePlus className={`h-4 w-4 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                </div>
                <span className={`font-light text-sm leading-5 `} >{t('ADD_FILES')}</span>
              </button>

              <input
                type="file"
                accept={getAcceptData()}
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                multiple
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="xl:hidden flex flex-col">
        <button
          className={`flex items-center gap-3 px-2 py-1.5  hover:bg-muted transition-colors text-left disabled:opacity-50 ${selectedModel?.modelType === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={handleAddPhotosClick}
          disabled={selectedModel?.modelType === 1}
        >
          <div className="flex h-4 w-4 items-center justify-center">
            <Image className={`h-4 w-4 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
          </div>
          <span className={`font-light text-sm leading-5`} >{t('ADD_PHOTOS')}</span>
        </button>

        <button
          className="flex items-center gap-3 px-2 py-1.5 hover:bg-muted transition-colors text-left disabled:opacity-50"
          onClick={handleAddFilesClick}
        >
          <div className="flex h-4 w-4 items-center justify-center">
            <CirclePlus className={`h-4 w-4 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
          </div>
          <span className={`font-light text-sm leading-5`} >{t('ADD_FILES')}</span>
        </button>

        <input
          type="file"
          accept={getAcceptData()}
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          multiple
        />
      </div>
    </div >
  );
}
